import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { getHighLevelContext } from '@/utils/highlevel';
import { SyncConfiguration } from '@/types';
import toast from 'react-hot-toast';

export function useSyncConfiguration() {
  const queryClient = useQueryClient();
  const context = getHighLevelContext();
  const locationId = context?.locationId || '';

  // Get sync configuration
  const {
    data: config,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['sync-config', locationId],
    queryFn: () => apiClient.getSyncConfiguration(locationId),
    enabled: !!locationId,
  });

  // Update sync configuration
  const updateConfigMutation = useMutation({
    mutationFn: (updates: Partial<SyncConfiguration>) =>
      config
        ? apiClient.updateSyncConfiguration(locationId, updates)
        : apiClient.createSyncConfiguration(locationId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync-config', locationId] });
      toast.success('Configuration updated successfully');
    },
    onError: (error) => {
      toast.error(`Failed to update configuration: ${error.message}`);
    },
  });

  return {
    config,
    isLoading,
    error,
    updateConfig: updateConfigMutation.mutate,
    isUpdating: updateConfigMutation.isPending,
  };
}
